import React, { memo, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import classNames from 'classnames';
import { createUseStyles, Icon, Markdown, Text, useTheme } from '@Sage/hoi-poi-ui';

import useBuildAnswer from 'shards/Copilot/hooks/useBuildAnswer';
import useLoadingPhrases from 'shards/Copilot/hooks/useLoadingPhrases';
import useTypewriterEffect from 'shards/Copilot/hooks/useTypewriterEffect';
import { useWeb5Context } from 'lib/Web5Context';
import { isEqual } from 'utils/objects';
import { getDateToUserTimezone, getMomentFromDateBackend, isSame, isToday } from 'utils/time';

import CustomLink from './CustomLink';
import VoiceMessage from '../VoiceMessage';

import styles from './styles';

const useStyles = createUseStyles(styles, 'CopilotComment');

// ========================================
// STATE MANAGEMENT
// ========================================
const actionTypes = {
    SET_ENHANCED_TEXT: 'SET_ENHANCED_TEXT',
    SET_IS_COPIED: 'SET_IS_COPIED',
    SET_STABLE_REPORTS: 'SET_STABLE_REPORTS',
    SET_STABLE_TABLES: 'SET_STABLE_TABLES',
};

const commentReducer = (state, action) => {
    switch (action.type) {
        case actionTypes.SET_ENHANCED_TEXT:
            return { ...state, enhancedText: action.payload };
        case actionTypes.SET_IS_COPIED:
            return { ...state, isCopied: action.payload };
        case actionTypes.SET_STABLE_REPORTS:
            return { ...state, stableReports: action.payload };
        case actionTypes.SET_STABLE_TABLES:
            return { ...state, stableTables: action.payload };
        default:
            return state;
    }
};

const Comment = memo(
    ({
        audioData,
        avatar,
        creationDate,
        delta,
        disableVote,
        finished,
        getChatBoxRef,
        interactionId,
        isError,
        isProgress,
        isUser,
        isWidget,
        loading,
        loadingInitialPhrase,
        loadingPhrases,
        onClick,
        onCopy,
        onContentUpdate,
        onEmail,
        onTypewriterComplete,
        onVote,
        reports,
        tables,
        text,
        typewriterEnabled = true,
        typewriterPaused,
        typewriterSpeed,
        vote,
    }) => {
        // ========================================
        // CONTEXT & CUSTOM HOOKS
        // ========================================
        const classes = useStyles();
        const theme = useTheme();
        const { t } = useWeb5Context();
        const { parseCopilotEmbeds } = useBuildAnswer();
        const loadingPhrase = useLoadingPhrases({
            initialPhrase: loadingInitialPhrase,
            isLoading: loading && !isUser && !text,
            phrases: loadingPhrases,
        });

        // Typewriter effect: only active for assistant messages while streaming
        const typewriterText = useTypewriterEffect({
            text: text || '',
            finished: !!finished,
            enabled: typewriterEnabled && !isUser && !isWidget,
            paused: !!typewriterPaused,
            onComplete: onTypewriterComplete,
            speed: typewriterSpeed,
        });

        // ========================================
        // REFS
        // ========================================
        const boxRef = useRef(null);
        const stableReportsRef = useRef(reports);
        const stableTablesRef = useRef(tables);

        // ========================================
        // LOCAL STATE
        // ========================================
        const [state, dispatch] = useReducer(commentReducer, {
            enhancedText: '',
            isCopied: false,
            stableReports: reports,
            stableTables: tables,
        });

        const { enhancedText, isCopied, stableReports, stableTables } = state;

        // ========================================
        // DERIVED STATE & MEMOIZATION
        // ========================================
        const date = useMemo(() => {
            if (!creationDate) return '';
            const dateMoment = getMomentFromDateBackend(creationDate, 'DD/MM/YYYY HH:mm:ss', true);

            if (isToday(dateMoment))
                return getDateToUserTimezone({ date: dateMoment, outputFormat: 'HH:mm A' });
            else if (isSame(dateMoment, null, 'year'))
                return getDateToUserTimezone({ date: dateMoment, outputFormat: 'DD/MM HH:mm A' });
            else
                return getDateToUserTimezone({
                    date: dateMoment,
                    outputFormat: 'DD/MM/YYYY HH:mm A',
                });
        }, [creationDate]);

        const showThumbUp = useMemo(() => {
            if (!onVote || disableVote) return false;
            if (isUser || !interactionId || !finished) return false;
            if (!vote || vote === '1') return true;
            return false;
        }, [disableVote, finished, interactionId, isUser, onVote, vote]);

        const showThumbDown = useMemo(() => {
            if (!onVote || disableVote) return false;
            if (isUser || !interactionId || !finished) return false;
            if (!vote || vote === '0') return true;
            return false;
        }, [disableVote, finished, interactionId, isUser, onVote, vote]);

        const markdownComponents = useMemo(
            () => ({
                a: (props) => (
                    <CustomLink
                        loading={!finished}
                        reports={stableReports}
                        tables={stableTables}
                        {...props}
                    />
                ),
            }),
            [finished, stableReports, stableTables],
        );

        const rootClasses = useMemo(
            () =>
                classNames(classes.root, {
                    'dana-chat-comment--clickable': !!onClick,
                    'dana-chat-comment--copied': isCopied,
                    'dana-chat-comment--copy': !isCopied && !isWidget && !!onCopy,
                }),
            [classes.root, isCopied, isWidget, onClick, onCopy],
        );

        const textContainerClasses = useMemo(
            () =>
                classNames('dana-chat-comment-text-container', {
                    'dana-chat-comment-user': isUser,
                }),
            [isUser],
        );

        const markdownClasses = useMemo(
            () =>
                classNames(classes.chatText, {
                    [classes.chatTextEllipsis]: isWidget,
                    [classes.chatTextLoading]: !!loadingPhrase,
                }),
            [
                classes.chatText,
                classes.chatTextEllipsis,
                classes.chatTextLoading,
                isWidget,
                loadingPhrase,
            ],
        );

        // ========================================
        // EVENT HANDLERS & CALLBACKS
        // ========================================
        const handleOnCopy = useCallback(() => {
            onCopy && onCopy();
            dispatch({ type: actionTypes.SET_IS_COPIED, payload: true });
            setTimeout(() => {
                dispatch({ type: actionTypes.SET_IS_COPIED, payload: false });
            }, 3000);
        }, [onCopy]);

        const handleOnEmail = useCallback(() => {
            const textBox = boxRef.current?.querySelector(
                '[class*="HoiPoi__CopilotComment__chatText"]',
            );
            const htmlContent = textBox?.innerHTML;
            onEmail && onEmail(htmlContent);
        }, [onEmail]);

        const handleRefCallback = useCallback(
            (ref) => {
                boxRef.current = ref;
                getChatBoxRef && getChatBoxRef({ interactionId, ref });
            },
            [getChatBoxRef, interactionId],
        );

        const handleThumbUp = useMemo(() => onVote && onVote('1'), [onVote]);
        const handleThumbDown = useMemo(() => onVote && onVote('0'), [onVote]);

        // ========================================
        // RENDER HELPERS
        // ========================================
        const renderActionButtons = useCallback(() => {
            if (!date && isProgress && !onCopy && !onEmail && !showThumbUp && !showThumbDown) {
                return null;
            }

            return (
                <div className="dana-chat-comment-actions">
                    {date && (
                        <Text
                            className="dana-chat-comment-date"
                            color={theme.colors.utility.textSecondary}
                            type="caption"
                        >
                            {date}
                        </Text>
                    )}
                    {!isProgress && (
                        <>
                            {onCopy && (
                                <div
                                    className="dana-chat-comment-copy__icon"
                                    onClick={!isCopied ? handleOnCopy : undefined}
                                >
                                    <Icon name={isCopied ? 'done' : 'contentCopy'} />
                                </div>
                            )}
                            {onEmail && !isUser && (
                                <div
                                    className="dana-chat-comment-email__icon"
                                    onClick={handleOnEmail}
                                >
                                    <Icon color={theme.colors.actionMinor[500]} name="email" />
                                </div>
                            )}
                            {showThumbUp && (
                                <div
                                    className={classNames('dana-chat-comment-thumb__icon', {
                                        'dana-chat-comment__voted': vote === '1',
                                    })}
                                    onClick={handleThumbUp}
                                >
                                    <Icon
                                        color={theme.colors.actionMinor[500]}
                                        name="thumbUpOutline"
                                    />
                                </div>
                            )}
                            {showThumbDown && (
                                <div
                                    className={classNames('dana-chat-comment-thumb__icon', {
                                        'dana-chat-comment__voted': vote === '0',
                                    })}
                                    onClick={handleThumbDown}
                                >
                                    <Icon
                                        color={theme.colors.actionMinor[500]}
                                        name="thumbDownOutline"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            );
        }, [
            date,
            handleOnCopy,
            handleOnEmail,
            handleThumbDown,
            handleThumbUp,
            isCopied,
            isProgress,
            isUser,
            onCopy,
            onEmail,
            showThumbDown,
            showThumbUp,
            theme.colors.actionMinor,
            theme.colors.utility.textSecondary,
            vote,
        ]);

        const renderContent = useCallback(() => {
            if (audioData?.batches?.length > 0) {
                return <VoiceMessage audioData={audioData} />;
            }

            // Show loading phrase when loading and no text content (only for assistant messages)
            const displayContent = loadingPhrase || enhancedText || typewriterText || '...';

            return (
                <Markdown
                    className={markdownClasses}
                    components={markdownComponents}
                    content={displayContent}
                    overrides={{ root: { skipHtml: true } }}
                />
            );
        }, [
            audioData,
            enhancedText,
            loadingPhrase,
            markdownClasses,
            markdownComponents,
            typewriterText,
        ]);

        const renderErrorMessage = useCallback(() => {
            if (!isError) return null;

            return (
                <div className="dana-chat-comment-error">
                    <Icon name="infoOutlined" />
                    <Text color={theme.colors.utility.textSecondary} type="body">
                        {t('label_dana_summary_error_not_sent')}
                    </Text>
                </div>
            );
        }, [isError, t, theme.colors.utility.textSecondary]);

        // ========================================
        // EFFECTS
        // ========================================
        useEffect(() => {
            const tablesChanged = !isEqual(stableTablesRef.current, tables);
            const reportsChanged = !isEqual(stableReportsRef.current, reports);

            if (tablesChanged) {
                stableTablesRef.current = tables;
                dispatch({ type: actionTypes.SET_STABLE_TABLES, payload: tables });
            }
            if (reportsChanged) {
                stableReportsRef.current = reports;
                dispatch({ type: actionTypes.SET_STABLE_REPORTS, payload: reports });
            }
        }, [reports, tables]);

        useEffect(() => {
            if (!typewriterText) {
                dispatch({ type: actionTypes.SET_ENHANCED_TEXT, payload: '' });
                return;
            }
            const enhanced = parseCopilotEmbeds(typewriterText);
            dispatch({ type: actionTypes.SET_ENHANCED_TEXT, payload: enhanced });
        }, [parseCopilotEmbeds, typewriterText]);

        useEffect(() => {
            if (!onContentUpdate || isUser || audioData?.batches?.length > 0) return;
            onContentUpdate();
        }, [audioData?.batches?.length, isUser, loadingPhrase, onContentUpdate, typewriterText]);

        // ========================================
        // RENDER
        // ========================================
        return (
            <div
                className={rootClasses}
                onClick={onClick}
                ref={handleRefCallback}
                {...(interactionId && { id: interactionId })}
            >
                <div className="dana-chat-comment-data">
                    {avatar && <div className="dana-chat-comment-avatar">{avatar}</div>}
                    <div className={textContainerClasses}>
                        {!isWidget && renderActionButtons()}
                        {renderContent()}
                    </div>
                </div>
                {renderErrorMessage()}
            </div>
        );
    },
);

export default Comment;
